// Who is signed in, and what they may do.
//
// One fetch on boot, shared by the nav and any screen that needs to hide a
// control. The server is the authority — this only stops people bumping into
// doors they can't open.

import { useEffect, useState } from "react";
import { api } from "./api";

export type Capability =
  | "schedule" | "clients" | "messages" | "clinical" | "sign_charts" | "review_charts"
  | "checkout" | "refund" | "reports" | "marketing" | "ads" | "forms" | "settings" | "staff";

export interface Me {
  id: string; name: string; email: string;
  staff_role: string; active: boolean;
  credential: string | null; provider_id: string | null;
  is_medical_director: boolean;
  capabilities: Capability[];
  /** Their own section list rather than the role's. */
  custom_permissions?: boolean;
  can_review_charts: boolean;
  /** Providers tick off completed paperwork; the director countersigns charts. */
  can_review_forms: boolean;
}

let cached: Me | null = null;
/** The practice's name, which travels with the person — see /staff/me. */
let workspaceName: string | null = null;
export function workspaceNameFromMe(): string | null { return workspaceName; }
const waiting: ((m: Me | null) => void)[] = [];
let inFlight = false;

/**
 * Does this person work in the practice, or were they given one narrow section?
 *
 * Somebody brought in to run the ads holds "ads" and nothing else. The chrome
 * - book a visit, search the clients, the front desk chat - belongs to people
 * who run the building, and every one of those buttons would answer 403 for
 * him. Hiding them is not security (the server decides that); it is not
 * showing somebody a door that is painted on.
 */
export function isPracticeStaff(me: Me | null): boolean {
  if (!me) return false;
  return ["schedule", "clients", "messages", "checkout", "clinical", "settings", "staff", "reports"]
    .some((c) => me.capabilities.includes(c as Capability));
}

export function useMe(): Me | null {
  const [me, setMe] = useState<Me | null>(cached);
  useEffect(() => {
    if (cached) { setMe(cached); return; }
    waiting.push(setMe);
    if (inFlight) return;
    inFlight = true;
    api.get<{ me: Me; workspace_name?: string | null }>("/staff/me")
      .then((d) => { cached = d.me; workspaceName = d.workspace_name ?? null; })
      .catch(() => { cached = null; })
      .finally(() => {
        inFlight = false;
        while (waiting.length) waiting.pop()!(cached);
      });
  }, []);
  return me;
}

export function useCan(capability: Capability): boolean {
  const me = useMe();
  // Until we know, assume yes — otherwise the nav visibly flickers on
  // every page load for the owner, who is most of the traffic.
  return !me || me.capabilities.includes(capability);
}

/** Has at least one of these sections (same optimism while loading). */
export function canAny(me: Me | null, caps: readonly Capability[] | Capability | undefined): boolean {
  if (!caps) return true;
  if (!me) return true;
  const list = Array.isArray(caps) ? caps : [caps as Capability];
  return list.some((c) => me.capabilities.includes(c));
}
